import { Grid, MenuItem, Typography } from '@material-ui/core';

import { Field } from 'formik';
import { isEqual, values } from 'lodash';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import { CURRENCY_LABEL_FORMATS, getCurrencyOptions, isIdEqual, sortCountriesUsFirst } from 'modules/enumerations/enumerationHelpers';
import { ChipSelect, Select, Switch, TextField } from 'modules/intake/form';
import { GlobalServiceInformation } from 'modules/intake/models';
//import GlobalVisaInfoCard from './globalVisaInfoCard.component';

const UNSAVED_CONTACTS_MESSAGE = 'There are unsaved family members. Please save the intake form to add them.';

const activeOptions = (options) => (options || []).filter((option) => option.isActive !== false);

// Global-port of the intake form from the main auth global service, shown for international moves only
const GlobalServiceForm = ({ globalService, contacts, enumerations, handleChange, handleBlur, setFieldValue }) => {
  // Newly added family members have id 0 until the form autosaves.
  // The API links visa records by id, so hold the selector until the save lands.
  const savedContacts = values(contacts).filter((instance) => !!instance.id);
  const hasUnsavedContacts = savedContacts.length !== values(contacts).length;
  const countries = sortCountriesUsFirst(activeOptions(enumerations.countries));
  const provinces = activeOptions(enumerations.provinces).filter((province) =>
    !globalService.homeCountryId || isIdEqual(province.countryId, globalService.homeCountryId),
  );
  const currencies = getCurrencyOptions(enumerations.currencies, CURRENCY_LABEL_FORMATS.LONG);

  // Keep one visa card per selected family member
  const handleVisaContactsChange = (event) => {
    handleChange(event);
    setFieldValue('globalService.visaInfos', GlobalServiceInformation.syncVisaInfos(globalService.visaInfos, event.target.value), false);
  };

  return (
    <div className="form-group">
      <Typography gutterBottom variant="h3">Global Services</Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Field
            component={ChipSelect}
            name="globalService.visaContacts"
            label="Family Members Using Visa/Immigration Services"
            value={globalService.visaContacts}
            onChange={handleVisaContactsChange}
            onBlur={handleBlur}
            fullWidth
            disabled={hasUnsavedContacts}
            helperText={hasUnsavedContacts ? UNSAVED_CONTACTS_MESSAGE : undefined}
            labelAccessor={(value) => `${value.firstName} ${value.lastName}`}
            labelWidth={300}
            style={{ marginBottom: 5 }}
          >
            {savedContacts.map((contact) => (
              <MenuItem key={contact.id} value={contact}>
                {`${contact.firstName} ${contact.lastName}`}
              </MenuItem>
            ))}
          </Field>
        </Grid>
        {/* <Grid item xs={12} md={6}>
          <Field
            component={Select}
            name="globalService.immigrationServiceProviderId"
            label="Immigration Service Provider"
            value={globalService.immigrationServiceProviderId}
            onChange={handleChange}
            onBlur={handleBlur}
            labelWidth={200}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {activeOptions(enumerations.immigrationServiceProviders).map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
            ))}
          </Field>
        </Grid> */}
        <Grid item xs={12} md={3}>
          <Field
            component={Select}
            name="globalService.homeCountryId"
            label="Home Country"
            value={globalService.homeCountryId}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {countries.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            component={Select}
            name="globalService.homeProvinceId"
            label="Home State"
            value={globalService.homeProvinceId}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {provinces.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            component={Select}
            name="globalService.citizenshipId"
            label="Citizenship"
            value={globalService.citizenshipId}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {countries.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            component={Select}
            name="globalService.payInCurrencyId"
            label="Pay In Currency"
            value={globalService.payInCurrencyId}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {currencies.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            component={Switch}
            name="globalService.isEligibleForTaxService"
            label="Eligible for Tax Service"
            value={globalService.isEligibleForTaxService}
            onChange={handleChange}
            onBlur={handleBlur}
            alignCenter
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            component={TextField}
            name="globalService.certificateOfCoverageExpiration"
            label="Certificate of Coverage Expiration"
            value={globalService.certificateOfCoverageExpiration}
            onChange={handleChange}
            onBlur={handleBlur}
            type="date"
          />
        </Grid>
      </Grid>
      {/* {globalService.visaInfos.length > 0 && (
        <div className="mt-2">
          <Typography variant="h4" gutterBottom>Visa/Immigration</Typography>
          {globalService.visaInfos.map((visaInfo, index) => (
            <GlobalVisaInfoCard
              key={visaInfo.intakeContactId}
              visaInfo={visaInfo}
              index={index}
              contact={values(contacts).find((instance) => instance.id === visaInfo.intakeContactId)}
              enumerations={enumerations}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          ))}
        </div>
      )} */}
    </div>
  );
};

GlobalServiceForm.propTypes = {
  globalService: PropTypes.object.isRequired,
  contacts: PropTypes.array.isRequired,
  enumerations: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

// The section forms skip re-renders unless their own slice of the form changed (see the other
// sections' shouldComponentUpdate). contacts is rebuilt on every render, so compare it by value.
const areEqual = (prev, next) =>
  prev.globalService === next.globalService &&
  isEqual(prev.contacts, next.contacts) &&
  prev.enumerations === next.enumerations &&
  prev.isSubmitting === next.isSubmitting;

export default memo(GlobalServiceForm, areEqual);
